import { computed, ref } from 'vue';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { buildAidboxQueryString, extractAidboxQueryData } from '@/composables/useAidboxQuery';

/**
 * The message inbox (#5331).
 *
 * Reads through the `message-inbox` AidboxQuery rather than fetching participant rows and then a
 * thread each. Three reasons, all from #5343's access model:
 *
 * 1. It would be an N+1, which CLAUDE.md prohibits.
 * 2. `messaging-thread-read` requires the thread id as `resource/id` or `_id` and will not
 *    satisfy a comma-separated multi-`_id` search, so "fetch 20 threads at once" isn't available.
 * 3. `_include` on the participant search would smuggle thread data past the patient-access check
 *    that policy puts on thread reads, because the policy binds to the request's resource/type.
 *
 * The query takes no identity parameter — it scopes itself with `{{user.practitioner.id}}` — so
 * there is nothing a caller can pass to read someone else's inbox.
 *
 * The state is module-scope on purpose, following `useNavBar`. Two components read this inbox at
 * once — the app-bar button in `DefaultLayout` and `MessagesPage` — and per-instance refs gave them
 * independent copies, so marking a thread read on the page left the app-bar dot lit until the next
 * page load. One shared source means the badge and the list cannot disagree.
 */
const threads = ref([]);
const loading = ref(false);

const unreadCount = computed(() => threads.value.filter((thread) => thread.unread).length);

/**
 * Drop the inbox on sign-out and on account switch (#5331).
 *
 * Module scope means this state outlives the components that render it, and signing out is pure
 * SPA state clearing — no reload — so without this the next user in the same tab inherits the
 * previous one's threads. That matters precisely because `fetchInbox` below degrades to the last
 * good list on failure: with no reset, a failed first fetch for user B leaves user A's client
 * names on screen indefinitely, and the skeleton stays hidden because the list is not empty.
 *
 * Called from the auth store's `clearClientAuthData`, which runs for both sign-out and the
 * switching-account path, so one hook covers both.
 */
export function resetMessageInbox() {
    threads.value = [];
    loading.value = false;
}

export function useMessageInbox() {
    const normalize = (row) => ({
        threadId: row.thread_id,
        participantRowId: row.participant_row_id,
        type: row.thread_type,
        status: row.thread_status,
        topic: row.thread_topic,
        authorId: row.author_id,
        lastMessageAt: row.last_message_at,
        lastReadAt: row.last_read_at,
        unread: row.unread === true,
    });

    const fetchInbox = async ({ limit = 100, offset = 0 } = {}) => {
        loading.value = true;
        try {
            const qs = buildAidboxQueryString({ _limit: limit, _offset: offset });
            const result = await $httpFhirApi.get(`/$query/message-inbox?${qs}`);
            threads.value = extractAidboxQueryData(result).map(normalize);
        } catch (error) {
            // Degrade to the last good list rather than blanking the inbox on a transient failure.
            // Safe only because `resetMessageInbox` clears the list on sign-out and account switch —
            // the "last good list" can therefore only ever be this user's own, in this account.
            console.error('useMessageInbox: failed to load the inbox', error);
        } finally {
            loading.value = false;
        }
    };

    /**
     * Mark a thread read by stamping lastReadAt on the caller's own participant row.
     *
     * `messaging-read-receipt` is the only write this composable makes, and it is scoped to the
     * caller's own row. Applied optimistically so the badge clears immediately, and rolled back
     * if the PATCH fails — a dot that wrongly persists is a smaller problem than one that wrongly
     * clears.
     */
    const markThreadRead = async (threadId) => {
        const thread = threads.value.find((candidate) => candidate.threadId === threadId);
        if (!thread || !thread.participantRowId) return;

        const previousUnread = thread.unread;
        const previousLastReadAt = thread.lastReadAt;
        const readAt = new Date().toISOString();

        thread.unread = false;
        thread.lastReadAt = readAt;

        try {
            await $httpFhirApi.patch(`/BN_MessageThreadParticipant/${thread.participantRowId}`, {
                lastReadAt: readAt,
            });
        } catch (error) {
            thread.unread = previousUnread;
            thread.lastReadAt = previousLastReadAt;
            console.error('useMessageInbox: failed to mark the thread read', error);
        }
    };

    return { threads, unreadCount, loading, fetchInbox, markThreadRead };
}

export default useMessageInbox;
